/**
 * WizardForm.init() - Imperative API for wizard forms
 *
 * Provides a simple way to initialize wizard forms from a script tag.
 *
 * @example
 * <script src="wizard-form.min.js"></script>
 * <script>
 *   WizardForm.init({
 *     container: '#signup-form',
 *     adapter: 'hubspot',
 *     hubspot: { portalId: '123', formId: 'abc' },
 *     onSubmit: (data) => console.log(data),
 *     onSuccess: () => console.log('Success!'),
 *     onError: (err) => console.error(err)
 *   });
 * </script>
 */

import type { WizardForm } from './components/wizard-form.js';
import type { SubmissionAdapter, SubmissionContext } from './adapters/types.js';
import { createHubSpotAdapter } from './adapters/hubspot.js';
import { createWebhookAdapter } from './adapters/webhook.js';
import { createRevenueHeroAdapter } from './adapters/revenuehero.js';

// Ensure components are registered
import './components/wizard-form.js';
import './components/wf-step.js';
import './components/wf-options.js';
import './components/wf-option.js';
import './components/wf-email.js';
import './components/wf-input.js';
import './components/wf-number.js';
import './components/wf-textarea.js';

export type FormData = Record<string, unknown>;

export interface WizardFormConfig {
  /**
   * Container element or selector
   */
  container: string | HTMLElement;

  /**
   * Adapter type or custom adapter
   */
  adapter?: 'hubspot' | 'webhook' | 'revenuehero' | 'json' | SubmissionAdapter;

  /**
   * HubSpot configuration
   */
  hubspot?: {
    portalId: string;
    formId: string;
    fieldMapping?: Record<string, string>;
  };

  /**
   * Webhook configuration
   */
  webhook?: {
    url: string;
    method?: 'POST' | 'PUT';
    headers?: Record<string, string>;
    fieldMapping?: Record<string, string>;
  };

  /**
   * RevenueHero configuration
   */
  revenuehero?: {
    routerId: string;
    fieldMapping?: Record<string, string>;
  };

  /**
   * Color theme
   */
  theme?: 'light' | 'dark' | 'auto';

  /**
   * Show progress bar
   */
  showProgress?: boolean;

  /**
   * Auto-advance after single-select option
   */
  autoAdvance?: boolean;

  /**
   * Mock mode (for testing)
   */
  mock?: boolean;

  /**
   * Callback before submission (can cancel)
   */
  onSubmit?: (data: FormData) => boolean | void;

  /**
   * Callback on successful submission
   */
  onSuccess?: (data: FormData, response?: unknown) => void;

  /**
   * Callback on submission error
   */
  onError?: (error: string, data?: FormData) => void;

  /**
   * Callback on step change
   */
  onStepChange?: (from: number, to: number, direction: 'forward' | 'backward') => void;

  /**
   * Callback on field value change
   */
  onFieldChange?: (name: string, value: unknown, formData: FormData) => void;

  /**
   * Transform form data before submission to adapter.
   * Called after validation, before adapter.submit().
   */
  serialize?: (data: FormData) => FormData;
}

export interface WizardFormInstance {
  /**
   * The wizard-form element
   */
  element: WizardForm;

  /**
   * Current form data
   */
  readonly formData: FormData;

  /**
   * Current step number
   */
  readonly currentStep: number;

  /**
   * Total number of steps
   */
  readonly totalSteps: number;

  /**
   * Go to next step
   */
  next(): Promise<void>;

  /**
   * Go to previous step
   */
  back(): void;

  /**
   * Go to a specific step
   */
  goToStep(step: number): void;

  /**
   * Submit the form
   */
  submit(): Promise<void>;

  /**
   * Reset the form
   */
  reset(): void;

  /**
   * Set form data
   */
  setFormData(data: FormData): void;

  /**
   * Destroy the instance (remove event listeners)
   */
  destroy(): void;

  /**
   * Serialize function (if configured)
   */
  serialize?: (data: FormData) => FormData;
}

/**
 * Initialize a wizard form with the given configuration
 *
 * @deprecated Use event-based API instead. Listen to wf:submit, wf:success, wf:error events
 * directly on the wizard-form element. See https://github.com/atomicwork/aw-wizard-forms
 */
export function init(config: WizardFormConfig): WizardFormInstance {
  console.warn(
    '[WizardForm] init() is deprecated. Use the event-based API instead:\n' +
    '  form.addEventListener("wf:submit", (e) => { ... });\n' +
    '  form.addEventListener("wf:success", (e) => { ... });\n' +
    'See https://github.com/atomicwork/aw-wizard-forms for migration guide.'
  );

  // Get container element
  const container =
    typeof config.container === 'string'
      ? document.querySelector<HTMLElement>(config.container)
      : config.container;

  if (!container) {
    throw new Error(`[WizardForm] Container not found: ${config.container}`);
  }

  // Find or create wizard-form element
  let wizardForm = container.querySelector<WizardForm>('wizard-form');
  if (!wizardForm) {
    wizardForm = container as unknown as WizardForm;
    if (wizardForm.tagName.toLowerCase() !== 'wizard-form') {
      throw new Error(
        '[WizardForm] Container must be a <wizard-form> element or contain one'
      );
    }
  }

  // Apply configuration attributes
  if (config.theme) {
    wizardForm.setAttribute('theme', config.theme);
  }
  if (config.showProgress !== undefined) {
    wizardForm.showProgress = config.showProgress;
  }
  if (config.autoAdvance !== undefined) {
    wizardForm.autoAdvance = config.autoAdvance;
  }
  if (config.mock) {
    wizardForm.mock = config.mock;
  }

  // Create adapter
  let adapter: SubmissionAdapter | null = null;

  if (typeof config.adapter === 'object') {
    // Custom adapter
    adapter = config.adapter;
  } else {
    const adapterType = config.adapter || 'json';

    switch (adapterType) {
      case 'hubspot':
        if (!config.hubspot?.portalId || !config.hubspot?.formId) {
          throw new Error('[WizardForm] HubSpot config requires portalId and formId');
        }
        adapter = createHubSpotAdapter({
          portalId: config.hubspot.portalId,
          formId: config.hubspot.formId,
          fieldMapping: config.hubspot.fieldMapping,
          mock: config.mock,
        });
        // Also set attributes for built-in HubSpot support
        wizardForm.setAttribute('hubspot-portal', config.hubspot.portalId);
        wizardForm.setAttribute('hubspot-form', config.hubspot.formId);
        break;

      case 'webhook':
        if (!config.webhook?.url) {
          throw new Error('[WizardForm] Webhook config requires url');
        }
        adapter = createWebhookAdapter({
          url: config.webhook.url,
          method: config.webhook.method,
          headers: config.webhook.headers,
          fieldMapping: config.webhook.fieldMapping,
          mock: config.mock,
        });
        break;

      case 'revenuehero':
        if (!config.revenuehero?.routerId) {
          throw new Error('[WizardForm] RevenueHero config requires routerId');
        }
        adapter = createRevenueHeroAdapter({
          routerId: config.revenuehero.routerId,
          fieldMapping: config.revenuehero.fieldMapping,
          mock: config.mock,
        });
        break;

      case 'json':
        // No adapter - just emit events
        adapter = null;
        break;
    }
  }

  // Event handlers
  const handleSubmit = (e: CustomEvent) => {
    const rawFormData = e.detail.formData;

    // Apply serialize transform if provided
    let submitData = { ...rawFormData };
    if (config.serialize) {
      submitData = config.serialize(submitData);
    }

    // Auto-expose test refs when mock mode is enabled
    if (config.mock) {
      (window as unknown as Record<string, unknown>).__wizardFormData = submitData;
      (window as unknown as Record<string, unknown>).__wizardFormRawData = rawFormData;
      (window as unknown as Record<string, unknown>).__wizardFormInstance = instance;
    }

    // Call user's onSubmit callback
    if (config.onSubmit) {
      const result = config.onSubmit(submitData);
      if (result === false) {
        e.preventDefault();
        return;
      }
    }

    // Submit via adapter if configured
    if (adapter) {
      e.preventDefault();

      const context: SubmissionContext = {
        pageUrl: window.location.href,
        pageTitle: document.title,
        referrer: document.referrer,
        timestamp: new Date().toISOString(),
      };

      adapter.submit(submitData, context).then((result) => {
        // Auto-expose response in mock mode
        if (config.mock) {
          (window as unknown as Record<string, unknown>).__wizardFormResponse = result;
        }

        if (result.success) {
          // Manually show success screen
          (wizardForm as unknown as { _submitted: boolean })._submitted = true;
          wizardForm!.requestUpdate();

          // Dispatch wf:success event (triggers handleSuccess listener + progress bar hide)
          wizardForm!.dispatchEvent(
            new CustomEvent('wf:success', {
              detail: { formData: rawFormData, response: result.data },
              bubbles: true,
              composed: true,
            })
          );
          // Note: onSuccess callback is triggered by handleSuccess listener
        } else {
          // Auto-expose error in mock mode
          if (config.mock) {
            (window as unknown as Record<string, unknown>).__wizardFormError = result.error;
          }

          if (config.onError) {
            // Pass raw form data (not serialized) for user callbacks
            config.onError(result.error || 'Submission failed', rawFormData);
          }
        }
      });
    }
  };

  const handleSuccess = (e: CustomEvent) => {
    // Auto-expose response in mock mode (for built-in HubSpot handler)
    if (config.mock) {
      (window as unknown as Record<string, unknown>).__wizardFormResponse = {
        success: true,
        data: e.detail.response,
      };
    }

    if (config.onSuccess) {
      config.onSuccess(e.detail.formData, e.detail.response);
    }
  };

  const handleError = (e: CustomEvent) => {
    // Auto-expose error in mock mode
    if (config.mock) {
      (window as unknown as Record<string, unknown>).__wizardFormError = e.detail.error;
    }

    if (config.onError) {
      config.onError(e.detail.error, e.detail.formData);
    }
  };

  const handleStepChange = (e: CustomEvent) => {
    if (config.onStepChange) {
      config.onStepChange(e.detail.from, e.detail.to, e.detail.direction);
    }
  };

  const handleFieldChange = (e: CustomEvent) => {
    if (config.onFieldChange) {
      config.onFieldChange(e.detail.name, e.detail.value, e.detail.formData);
    }
  };

  // Attach event listeners
  wizardForm.addEventListener('wf:submit', handleSubmit as EventListener);
  wizardForm.addEventListener('wf:success', handleSuccess as EventListener);
  wizardForm.addEventListener('wf:error', handleError as EventListener);
  wizardForm.addEventListener('wf:step-change', handleStepChange as EventListener);
  wizardForm.addEventListener('wf:field-change', handleFieldChange as EventListener);

  // Create instance
  const instance: WizardFormInstance = {
    element: wizardForm,

    get formData() {
      return wizardForm!.formData;
    },

    get currentStep() {
      return wizardForm!.currentStep;
    },

    get totalSteps() {
      return wizardForm!.totalSteps;
    },

    next() {
      return wizardForm!.next();
    },

    back() {
      wizardForm!.back();
    },

    goToStep(step: number) {
      wizardForm!.goToStep(step);
    },

    submit() {
      return wizardForm!.submit();
    },

    reset() {
      wizardForm!.reset();
    },

    setFormData(data: FormData) {
      wizardForm!.setFormData(data);
    },

    destroy() {
      wizardForm!.removeEventListener('wf:submit', handleSubmit as EventListener);
      wizardForm!.removeEventListener('wf:success', handleSuccess as EventListener);
      wizardForm!.removeEventListener('wf:error', handleError as EventListener);
      wizardForm!.removeEventListener('wf:step-change', handleStepChange as EventListener);
      wizardForm!.removeEventListener('wf:field-change', handleFieldChange as EventListener);
    },

    // Expose serialize function if configured
    serialize: config.serialize,
  };

  return instance;
}

export default { init };
